import {SpanHighlight} from './PdfViewer.types';
import {hasPdfCoordinates} from './spanMath';
import {HibuTag, ProductDocument} from '../../search/search-types';
import {SpotlightTagType} from '../../config/config-types';

/**
 * A drawable tag occurrence: a `PdfHighlight`, which the Spotlight panel and navigation consume, carrying
 * its source `span` for the overlay.
 */
export type TagHighlight = SpanHighlight;

/**
 * `mapTag` leaves `label` undefined when a tag has none in the display or the document language. Falls back
 * to the tagId's key segment (`annotator:type:key`), or the row has no title. `HibuTag` types both fields as
 * required `string` and neither holds, hence the optional chain.
 */
function panelLabel(tag: HibuTag): string {
    return tag.label || tag.id?.split(':')[2] || tag.id || '';
}

/**
 * One entry per tag occurrence (span), keyed by `${tag.id}#${spanIndex}` so the overlay div and the
 * Spotlight-click navigation target the same element.
 *
 * The array is also dispatched as `documentSelectedTags` and is what the Spotlight panel lists, so
 * filtering here costs panel rows, and a tag type that loses every span loses its whole section.
 *
 * Spans without coordinates are skipped: nothing on the client can place them.
 */
export function buildSpotlightTagHighlights(
    doc: ProductDocument | undefined,
    spotlightTagTypes: SpotlightTagType[],
): TagHighlight[] {
    const out: TagHighlight[] = [];
    if (!doc?.hibuTags) {
        return out;
    }
    for (const tagType of spotlightTagTypes) {
        const tags = doc.hibuTags[tagType.name] ?? [];
        for (const tag of tags) {
            const spans = tag.spans ?? [];
            for (let j = 0; j < spans.length; j++) {
                const span = spans[j];
                if (!hasPdfCoordinates(span)) {
                    continue;
                }
                out.push({
                    id: `${tag.id}#${j}`,
                    page: span.pageNumber,
                    subtype: 'Highlight',
                    rect: [
                        span.bottomLeftX,
                        span.bottomLeftY,
                        span.bottomLeftX + span.width,
                        span.bottomLeftY + span.height,
                    ],
                    rotation: 0,
                    color: new Uint8ClampedArray([255, 179, 0]),
                    annotationType: 0,
                    // ends with the tag-type name so the Spotlight panel's grouping (`endsWith(htt.name)`) matches
                    contentsObj: {str: `HIBU tag ${tagType.name}`, dir: 'ltr'},
                    titleObj: {str: panelLabel(tag), dir: 'ltr'},
                    span,
                });
            }
        }
    }
    // Panel order: page, then down the page, then left to right. Y descends
    // because the PDF origin is bottom-left.
    out.sort(
        (a, b) =>
            a.page - b.page ||
            b.span.bottomLeftY - a.span.bottomLeftY ||
            a.span.bottomLeftX - b.span.bottomLeftX,
    );
    return out;
}
